/**
 * Vanilla JS wrapper for the FeedbackButton.
 * Renders a standalone feedback form without React dependency.
 */
import { UITLApiClient } from '../api';
import type { UITLConfig } from '../types';

export class VanillaFeedbackWidget {
  private api: UITLApiClient;
  private container: HTMLElement;
  private isOpen = false;
  private feedbackType: 'bug' | 'feature' | 'feedback' = 'feedback';
  private submitted = false;
  private submitting = false;
  private error = '';
  private primaryColor: string;

  constructor(config: UITLConfig) {
    this.api = new UITLApiClient(config);
    this.primaryColor = config.theme?.primaryColor || '#2563EB';
    this.container = document.createElement('div');
    this.container.id = 'uitl-feedback-widget';
    document.body.appendChild(this.container);
    this.render();
  }

  private toggle() {
    this.isOpen = !this.isOpen;
    this.submitted = false;
    this.error = '';
    this.render();
  }

  private async submit() {
    const title = (this.container.querySelector('#uitl-fb-title') as HTMLInputElement)?.value;
    const text = (this.container.querySelector('#uitl-fb-text') as HTMLTextAreaElement)?.value;
    if (!title || !text) { this.error = 'Title and description are required.'; this.render(); return; }

    this.submitting = true;
    this.error = '';
    this.render();

    try {
      await this.api.submitFeedback({
        feedback_type: this.feedbackType,
        title,
        feedback_text: text,
        page_path: window.location.pathname,
        user_context: {
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString(),
        },
      });
      this.submitted = true;
    } catch (e) {
      this.error = 'Failed to submit. Please try again.';
    }
    this.submitting = false;
    this.render();
  }

  private setType(type: 'bug' | 'feature' | 'feedback') {
    this.feedbackType = type;
    this.render();
  }

  private render() {
    const c = this.primaryColor;
    if (!this.isOpen) {
      this.container.innerHTML = `
        <button id="uitl-fb-fab" style="position:fixed;bottom:90px;right:24px;width:48px;height:48px;border-radius:50%;background:${c};color:white;border:none;cursor:pointer;font-size:20px;box-shadow:0 4px 12px rgba(0,0,0,0.3);z-index:99998;display:flex;align-items:center;justify-content:center;">📝</button>
      `;
      this.container.querySelector('#uitl-fb-fab')?.addEventListener('click', () => this.toggle());
      return;
    }

    if (this.submitted) {
      this.container.innerHTML = `
        <div style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;">
          <div style="background:white;border-radius:16px;padding:32px;max-width:400px;width:90%;text-align:center;">
            <div style="font-size:48px;margin-bottom:12px;">✅</div>
            <h3 style="margin:0 0 8px;font-size:18px;">Thank you!</h3>
            <p style="color:#6b7280;margin:0 0 20px;font-size:14px;">Your feedback has been submitted.</p>
            <button id="uitl-fb-close" style="padding:8px 24px;background:${c};color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;">Close</button>
          </div>
        </div>
      `;
      this.container.querySelector('#uitl-fb-close')?.addEventListener('click', () => this.toggle());
      return;
    }

    const types = [
      { key: 'bug', label: '🐛 Bug', active: this.feedbackType === 'bug' },
      { key: 'feature', label: '💡 Feature', active: this.feedbackType === 'feature' },
      { key: 'feedback', label: '💬 Feedback', active: this.feedbackType === 'feedback' },
    ];

    const typeBtns = types.map(t =>
      `<button class="uitl-type-btn" data-type="${t.key}" style="flex:1;padding:8px;border:1px solid ${t.active ? c : '#d1d5db'};background:${t.active ? c : 'white'};color:${t.active ? 'white' : '#374151'};border-radius:8px;cursor:pointer;font-size:13px;">${t.label}</button>`
    ).join('');

    this.container.innerHTML = `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;">
        <div style="background:white;border-radius:16px;padding:24px;max-width:440px;width:90%;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
            <h3 style="margin:0;font-size:18px;">Send Feedback</h3>
            <button id="uitl-fb-x" style="background:none;border:none;font-size:20px;cursor:pointer;color:#9ca3af;">✕</button>
          </div>
          <div style="display:flex;gap:8px;margin-bottom:16px;">${typeBtns}</div>
          <input id="uitl-fb-title" type="text" placeholder="Title" style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;margin-bottom:12px;box-sizing:border-box;" />
          <textarea id="uitl-fb-text" placeholder="Describe the issue or idea..." rows="4" style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;resize:vertical;margin-bottom:12px;box-sizing:border-box;"></textarea>
          ${this.error ? `<p style="color:#ef4444;font-size:13px;margin:0 0 12px;">${this.error}</p>` : ''}
          <button id="uitl-fb-submit" style="width:100%;padding:10px;background:${c};color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:600;" ${this.submitting ? 'disabled' : ''}>${this.submitting ? 'Submitting...' : 'Submit'}</button>
        </div>
      </div>
    `;

    this.container.querySelector('#uitl-fb-x')?.addEventListener('click', () => this.toggle());
    this.container.querySelector('#uitl-fb-submit')?.addEventListener('click', () => this.submit());
    this.container.querySelectorAll('.uitl-type-btn').forEach(btn => {
      btn.addEventListener('click', () => this.setType((btn as HTMLElement).dataset.type as any));
    });
  }

  destroy() {
    this.container.remove();
  }
}
